import { Search } from '@components/shared/search/Search';
import type { CampaignType } from '@features/campaign-list';
import type { Advertiser, Client } from '@features/clients';
import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import type { ExportFilterState } from './filter-state';

const EXPORT_CAMPAIGN_TYPES: { value: CampaignType; label: string }[] = [
  { value: 'visitation', label: 'Visitation' },
  { value: 'rsl', label: 'RSL' },
  { value: 'rslvisits', label: 'RSL + Visitation' },
];

const isExportCampaignType = (value: string): value is CampaignType =>
  EXPORT_CAMPAIGN_TYPES.some(t => t.value === value);

type ExportFiltersProps = {
  clients: Client[];
  filterState: ExportFilterState;
  isLoading: boolean;
  onFilterChange: (state: ExportFilterState) => void;
};

export const ExportFilters = ({
  clients,
  filterState,
  isLoading,
  onFilterChange: handleFilterChange,
}: ExportFiltersProps) => {
  const {
    selectedClient,
    selectedAdvertiser,
    selectedCampaignType,
    searchText,
  } = filterState;

  const advertisers = selectedClient?.advertisers ?? [];

  const handleClientChange = (client: Client | null) => {
    handleFilterChange({
      ...filterState,
      selectedClient: client,
      selectedAdvertiser: null,
    });
  };

  const handleAdvertiserChange = (advertiser: Advertiser | null) => {
    handleFilterChange({ ...filterState, selectedAdvertiser: advertiser });
  };

  const handleCampaignTypeChange = (value: string) => {
    if (isExportCampaignType(value)) {
      handleFilterChange({ ...filterState, selectedCampaignType: value });
    }
  };

  return (
    <Stack alignItems="center" direction="row" gap={2} marginTop={2}>
      <Autocomplete
        data-testid="organization-select"
        disabled={isLoading || clients.length <= 1}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        loading={isLoading}
        onChange={(_, newValue) => handleClientChange(newValue)}
        options={clients}
        renderInput={params => <TextField {...params} label="Organization" />}
        size="small"
        sx={{ minWidth: 180 }}
        value={selectedClient}
      />

      <Autocomplete
        data-testid="advertiser-select"
        disabled={advertisers.length === 0}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        onChange={(_, newValue) => handleAdvertiserChange(newValue)}
        options={advertisers}
        renderInput={params => <TextField {...params} label="Advertiser" />}
        size="small"
        sx={{ minWidth: 180 }}
        value={selectedAdvertiser}
      />

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Campaign Type</InputLabel>
        <Select
          data-testid="campaign-type-select"
          label="Campaign Type"
          onChange={e => handleCampaignTypeChange(e.target.value)}
          value={selectedCampaignType}
        >
          {EXPORT_CAMPAIGN_TYPES.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Search
        onChange={e =>
          handleFilterChange({ ...filterState, searchText: e.target.value })
        }
        textFieldProps={{ sx: { marginLeft: 'auto', width: '20rem' } }}
        value={searchText}
      />
    </Stack>
  );
};
